import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './RegisterStation.css';

const STATES = [
  'Andhra Pradesh','Telangana','Karnataka','Tamil Nadu','Maharashtra',
  'Gujarat','Rajasthan','Uttar Pradesh','Madhya Pradesh','West Bengal',
  'Kerala','Punjab','Haryana','Bihar','Odisha','Other',
];

const EMPTY = {
  name: '', address: '', gstin: '', state: '', phone: '',
};

/**
 * Register New Station (parent–sub model).
 *
 * The logged-in user submits a request to add another fuel station under
 * their existing account. The new station is created with status='Pending'
 * and is_active=0 — invisible until the Noris team approves it.
 *
 * Notes:
 *   • No new login is created. Same client_id, new fuel_stations row.
 *   • Once approved, the station appears in the station-selector dropdown
 *     in the top header and can be used like any other station.
 *   • Existing data is never affected — each station has its own data scope.
 */
export default function RegisterStation() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [form, setForm]       = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(null);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) return t('registerStation.errors.nameRequired');
    if (form.gstin && !/^[0-9A-Z]{15}$/.test(form.gstin.trim().toUpperCase())) {
      return t('registerStation.errors.gstinInvalid');
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true); setError(''); setSuccess(null);
    try {
      const data = await api.stations.register({
        name:    form.name.trim(),
        address: form.address.trim(),
        gstin:   form.gstin.trim().toUpperCase(),
        state:   form.state.trim(),
        phone:   form.phone.trim(),
      });
      setSuccess(data);
      setForm(EMPTY);
    } catch (err) {
      setError(err.message || t('registerStation.errors.submitFailed'));
    }
    setLoading(false);
  };

  const reset = () => { setForm(EMPTY); setError(''); setSuccess(null); };

  /* ─── Success view ─── */
  if (success) {
    return (
      <div className="rs-page">
        <div className="page-header">
          <h2 className="page-title">{t('registerStation.title')}</h2>
        </div>

        <div className="rs-success-card">
          <div className="rs-success-icon">⏳</div>
          <h3 className="rs-success-title">{t('registerStation.pendingTitle')}</h3>
          <p className="rs-success-msg">
            <strong>{success.name}</strong> {t('registerStation.successMsg1')} <em>{t('common.pending')}</em> {t('registerStation.successMsg2')}
          </p>

          <div className="rs-status-pills">
            <div className="rs-pill rs-pill-active">
              <span className="rs-pill-step">1</span>
              <span>{t('registerStation.stepSubmitted')}</span>
            </div>
            <div className="rs-pill-arrow">→</div>
            <div className="rs-pill rs-pill-active">
              <span className="rs-pill-step">2</span>
              <span>{t('registerStation.stepPendingReview')}</span>
            </div>
            <div className="rs-pill-arrow">→</div>
            <div className="rs-pill">
              <span className="rs-pill-step">3</span>
              <span>{t('common.approved')}</span>
            </div>
          </div>

          <div className="rs-info-box">
            ℹ <strong>{t('registerStation.whatNext')}</strong> {t('registerStation.whatNextBody1')} (<code>{user?.email}</code>) {t('registerStation.whatNextBody2')}
          </div>

          <div className="rs-success-actions">
            <button className="btn btn-primary" onClick={reset}>＋ {t('registerStation.registerAnother')}</button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Form view ─── */
  return (
    <div className="rs-page">
      <div className="page-header">
        <h2 className="page-title">{t('registerStation.title')}</h2>
      </div>

      <div className="rs-intro">
        {t('registerStation.intro')}
      </div>

      {error && <div className="rs-error">⚠️ {error}</div>}

      <form onSubmit={submit} autoComplete="off" className="rs-form">
        <div className="section-card">
          <div className="section-header">{t('registerStation.stationDetails')}</div>
          <div className="rs-grid">

            <div className="form-group rs-col-2">
              <label>{t('registerStation.stationName')} <span className="req">*</span></label>
              <input type="text" placeholder={t('registerStation.namePlaceholder')}
                     value={form.name} onChange={f('name')} required />
            </div>

            <div className="form-group">
              <label>{t('registerStation.gstin')}</label>
              <input type="text" placeholder="36XXXXX1234X1ZX"
                     value={form.gstin}
                     onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                     maxLength={15} />
              <div className="form-hint">{t('registerStation.gstinHint')}</div>
            </div>

            <div className="form-group">
              <label>{t('registerStation.stateLabel')}</label>
              <select value={form.state} onChange={f('state')}>
                <option value="">{t('registerStation.selectPlaceholder')}</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>{t('registerStation.phone')}</label>
              <input type="tel" placeholder={t('registerStation.phonePlaceholder')}
                     value={form.phone} onChange={f('phone')} maxLength={15} />
            </div>

            <div className="form-group rs-col-2">
              <label>{t('registerStation.stationAddress')}</label>
              <input type="text" placeholder={t('registerStation.addressPlaceholder')}
                     value={form.address} onChange={f('address')} />
            </div>
          </div>
        </div>

        <div className="rs-info-box">
          ℹ <strong>{t('registerStation.formInfo1')} ({user?.email}).</strong> {t('registerStation.formInfo2')}
        </div>

        <div className="rs-actions">
          <button type="button" className="btn btn-light" onClick={reset} disabled={loading}>{t('common.reset')}</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? `⏳ ${t('registerStation.submitting')}` : `🚀 ${t('registerStation.submitForApproval')}`}
          </button>
        </div>
      </form>
    </div>
  );
}
